import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { patientApi, type HospitalPanel, type HospitalSummary } from '../../api';
import type { MarketplaceProvider } from '../../types';

// ─── Icons ───────────────────────────────────────────────────────────────────

function IconSearch({ color }: { color: string }) {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
    </svg>
  );
}

function IconHospital({ color }: { color: string }) {
  return (
    <svg width={26} height={26} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 21h18" /><path d="M5 21V7l7-4 7 4v14" />
      <rect x="9" y="10" width="6" height="6" />
      <path d="M12 10v6" /><path d="M9 13h6" />
    </svg>
  );
}

function IconStar({ color }: { color: string }) {
  return (
    <svg width={12} height={12} viewBox="0 0 24 24" fill={color} stroke={color} strokeWidth="1.5">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );
}

function IconLocation({ color }: { color: string }) {
  return (
    <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function IconClock({ color }: { color: string }) {
  return (
    <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
    </svg>
  );
}

// ─── Hospital Card ────────────────────────────────────────────────────────────

function HospitalCard({ provider }: { provider: MarketplaceProvider }) {
  const navigate = useNavigate();
  const hasEmergency = provider.tags.some(
    (t) => t.toLowerCase().includes('emergency') || t.toLowerCase().includes('24x7') || t.toLowerCase().includes('24/7')
  );

  return (
    <div
      style={{
        background: T.white,
        border: `1px solid ${T.border}`,
        borderRadius: 16,
        padding: 16,
        boxShadow: T.shadow,
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 10 }}>
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 14,
            background: '#DC262612',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <IconHospital color="#DC2626" />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ fontFamily: T.font, fontSize: 14, fontWeight: 700, color: T.text, lineHeight: 1.3 }}>
              {provider.name}
            </span>
            {provider.isVerified && (
              <span
                style={{
                  fontFamily: T.font,
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#059669',
                  background: '#05966915',
                  border: '1px solid #05966930',
                  borderRadius: 20,
                  padding: '2px 7px',
                }}
              >
                ✓ Verified
              </span>
            )}
            {hasEmergency && (
              <span
                style={{
                  fontFamily: T.font,
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#DC2626',
                  background: '#FEF2F2',
                  border: '1px solid #FCA5A5',
                  borderRadius: 20,
                  padding: '2px 7px',
                }}
              >
                ⚡ Emergency
              </span>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {provider.city && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontFamily: T.font, fontSize: 11, color: T.textSec }}>
                <IconLocation color={T.textTer} />{provider.city}
              </span>
            )}
            {provider.openHours && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontFamily: T.font, fontSize: 11, color: T.textSec }}>
                <IconClock color={T.textTer} />{provider.openHours}
              </span>
            )}
          </div>
        </div>

        {/* Rating */}
        {provider.rating > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 3, background: '#FFF7ED', borderRadius: 8, padding: '4px 8px', flexShrink: 0 }}>
            <IconStar color="#F59E0B" />
            <span style={{ fontFamily: T.font, fontSize: 12, fontWeight: 700, color: '#92400E' }}>
              {provider.rating.toFixed(1)}
            </span>
          </div>
        )}
      </div>

      {/* Department chips from specialties or specialization */}
      {((provider.specialties && provider.specialties.length > 0) || ((provider as any).specialization && (provider as any).specialization.length > 0)) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {(provider.specialties ?? (provider as any).specialization ?? []).slice(0, 5).map((spec: string) => (
            <span
              key={spec}
              style={{
                fontFamily: T.font,
                fontSize: 10,
                fontWeight: 600,
                color: '#DC2626',
                background: '#DC262610',
                border: '1px solid #DC262620',
                borderRadius: 20,
                padding: '3px 8px',
              }}
            >
              {spec}
            </span>
          ))}
          {provider.specialties.length > 5 && (
            <span style={{ fontFamily: T.font, fontSize: 10, color: T.textSec, padding: '3px 0' }}>
              +{provider.specialties.length - 5} more
            </span>
          )}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={() => navigate(`/patient/care-services/hospitals/${provider.slug}`)}
          style={{
            flex: 1,
            fontFamily: T.font,
            fontSize: 12,
            fontWeight: 700,
            color: '#DC2626',
            background: '#DC262610',
            border: '1.5px solid #DC262630',
            borderRadius: 10,
            padding: '9px 0',
            cursor: 'pointer',
          }}
        >
          View Hospital
        </button>
        <button
          onClick={() => navigate(`/patient/care-services/hospitals/${provider.slug}/book`)}
          style={{
            flex: 1,
            fontFamily: T.font,
            fontSize: 12,
            fontWeight: 700,
            color: T.white,
            background: '#DC2626',
            border: 'none',
            borderRadius: 10,
            padding: '9px 0',
            cursor: 'pointer',
          }}
        >
          Book OPD
        </button>
      </div>
    </div>
  );
}

/**
 * A hospital that runs its own public panel.
 *
 * `GET /hospitals` is the hospital module's own directory: departments,
 * doctors, a price list, licences and real OPD booking — and a hospital
 * appears in it only once it has switched that panel on. This page never read
 * it, so the day a hospital turns its panel on nothing here would show it, and
 * everything the panel publishes stayed invisible. It is empty on this network
 * today; the marketplace listings below are what a patient can book right now.
 */
function PanelHospitalCard({ panel }: { panel: HospitalPanel }) {
  const navigate = useNavigate();
  const typeLine = [panel.hospitalType, panel.ownershipType].filter(Boolean).join(' · ');
  const place = [panel.addressLine, panel.city, panel.state, panel.pincode].filter(Boolean).join(', ');
  const facilities = panel.facilities ?? [];
  // Shown openly rather than dropped: what the licence covers may be
  // unavailable.
  const adverse = (panel.licences ?? []).filter((l) => !/valid|active/i.test(l.status || ''));

  return (
    <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 16, padding: 16, boxShadow: T.shadow }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <div style={{ width: 42, height: 42, borderRadius: 8, flexShrink: 0, background: '#DC26261F', display: 'grid', placeItems: 'center' }}>
          <IconHospital color="#DC2626" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: T.font, fontSize: 14, fontWeight: 700, color: T.text }}>{panel.displayName}</span>
            {panel.verified && (
              <span style={{ fontFamily: T.font, fontSize: 10, fontWeight: 700, color: '#059669', background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 20, padding: '2px 7px' }}>
                ✓ Verified
              </span>
            )}
            <span style={{ fontFamily: T.font, fontSize: 10, fontWeight: 700, color: '#1D4ED8', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 20, padding: '2px 7px' }}>
              Hospital panel
            </span>
          </div>
          {typeLine && <div style={{ fontFamily: T.font, fontSize: 12, color: T.textSec, marginTop: 2 }}>{typeLine}</div>}
          {place && <div style={{ fontFamily: T.font, fontSize: 11, color: T.textTer, marginTop: 2 }}>{place}</div>}
        </div>
      </div>

      {(panel.emergency?.available || facilities.length > 0) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
          {panel.emergency?.available && (
            <span style={{ fontFamily: T.font, fontSize: 10, fontWeight: 700, color: '#DC2626', background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 20, padding: '3px 8px' }}>
              ⚡ Emergency
            </span>
          )}
          {facilities.slice(0, 4).map((f) => (
            <span key={f.key} style={{ fontFamily: T.font, fontSize: 10, fontWeight: 600, color: T.navy, background: `${T.navy}0D`, border: `1px solid ${T.navy}20`, borderRadius: 20, padding: '3px 8px' }}>
              {f.label}
            </span>
          ))}
        </div>
      )}

      {adverse.length > 0 && (
        <div style={{ fontFamily: T.font, fontSize: 10.5, color: '#B45309', marginTop: 10, lineHeight: 1.5 }}>
          ⚠ {adverse.map((l) => `${l.title} · ${l.status}`).join(' · ')}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button
          onClick={() => navigate(`/patient/care-services/hospital-panel/${panel.id}`)}
          style={{
            flex: 1, fontFamily: T.font, fontSize: 12, fontWeight: 700, color: '#DC2626',
            background: '#DC262610', border: '1.5px solid #DC262630', borderRadius: 10, padding: '9px 0', cursor: 'pointer',
          }}
        >
          View Hospital
        </button>
        {panel.publicBookingEnabled && (
          <button
            onClick={() => navigate(`/patient/care-services/hospital-panel/${panel.id}?book=1`)}
            style={{
              flex: 1, fontFamily: T.font, fontSize: 12, fontWeight: 700, color: T.white,
              background: '#DC2626', border: 'none', borderRadius: 10, padding: '9px 0', cursor: 'pointer',
            }}
          >
            Book OPD
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function HospitalDiscovery() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [providers, setProviders] = useState<MarketplaceProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // The hospital module's own directory. It is separate from the marketplace
  // listing on purpose, and empty until a hospital switches its panel on — so
  // a failure here leaves the page exactly as it was, with no section shown.
  const [panels, setPanels] = useState<HospitalPanel[]>([]);
  useEffect(() => {
    let alive = true;
    patientApi.hospital.directory({ limit: 20 })
      .then((d) => { if (alive) setPanels(d?.items ?? []); })
      .catch(() => { if (alive) setPanels([]); });
    return () => { alive = false; };
  }, []);

  /**
   * The patient's own hospital record. This page had no route to it, and this
   * account has three open visits and an unpaid bill sitting on it unseen —
   * the app opens exactly that from its Hospitals screen.
   */
  const [record, setRecord] = useState<HospitalSummary | null>(null);
  useEffect(() => {
    let alive = true;
    patientApi.hospital.summary()
      .then((r) => { if (alive) setRecord(r); })
      .catch(() => { if (alive) setRecord(null); });
    return () => { alive = false; };
  }, []);

  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const fetchProviders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const verified = activeFilters.includes('Verified') ? 'true' : undefined;
      const result = await patientApi.getMarketplaceProviders({
        type: 'HOSPITAL',
        search: search || undefined,
        verified,
        limit: 20,
      });
      let list = result.providers ?? [];
      // Client-side filter: Emergency available
      if (activeFilters.includes('Emergency available')) {
        list = list.filter((p: MarketplaceProvider) =>
          p.tags.some(
            (t) => {
              // Whole-word match only — a bare `includes('er')` wrongly
              // flagged tags like "Delivery" as an emergency room.
              const tl = t.toLowerCase();
              return tl.includes('emergency') || tl.includes('24/7') || tl.includes('24x7') || /\ber\b/.test(tl);
            }
          )
        );
      }
      // Client-side filter: Verified
      if (activeFilters.includes('Verified')) {
        list = list.filter((p: MarketplaceProvider) => p.isVerified);
      }
      if (aliveRef.current) setProviders(list);
    } catch {
      if (aliveRef.current) { setProviders([]); setError('Could not load hospitals. Please try again.'); }
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, [search, activeFilters]);

  useEffect(() => {
    const timer = setTimeout(fetchProviders, 350);
    return () => clearTimeout(timer);
  }, [fetchProviders]);

  const toggleFilter = (chip: string) => {
    setActiveFilters((prev) =>
      prev.includes(chip) ? prev.filter((f) => f !== chip) : [...prev, chip]
    );
  };

  return (
    <div style={{ background: T.bg, fontFamily: T.font, margin: '0 auto', paddingBottom: 100 }}>
      {/* Header */}
      <div
        style={{
          background: `linear-gradient(135deg, #DC2626 0%, #991B1B 100%)`,
          padding: '20px 16px 56px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{ position: 'absolute', top: -30, right: -30, width: 140, height: 140, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
        <h1 style={{ fontFamily: T.font, fontSize: 24, fontWeight: 800, color: T.white, margin: '0 0 6px' }}>
          Hospitals & OPD
        </h1>
        <p style={{ fontFamily: T.font, fontSize: 13, color: 'rgba(255,255,255,0.75)', margin: 0 }}>
          Book OPD appointments, explore departments
        </p>
      </div>

      {/* Search */}
      <div style={{ padding: '0 16px', marginTop: -28, position: 'relative', zIndex: 10 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: T.white,
            borderRadius: 14,
            padding: '11px 14px',
            boxShadow: T.shadowLg ?? '0 4px 20px rgba(0,0,0,0.12)',
            border: `1px solid ${T.border}`,
          }}
        >
          <IconSearch color={T.textTer} />
          <input
            type="text"
            placeholder="Search hospitals, departments..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              fontFamily: T.font,
              fontSize: 13,
              color: T.text,
              background: 'transparent',
              padding: 0,
            }}
          />
          {search && (
            <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.textTer, fontSize: 16, lineHeight: 1, padding: 0 }}>
              ×
            </button>
          )}
        </div>
      </div>

      {/* Filter chips */}
      <div style={{ padding: '14px 16px 0', overflowX: 'auto' }}>
        <div style={{ display: 'flex', gap: 8, width: 'max-content' }}>
          {['Verified', 'Emergency available'].map((chip) => {
            const active = activeFilters.includes(chip);
            return (
              <button
                key={chip}
                onClick={() => toggleFilter(chip)}
                style={{
                  fontFamily: T.font,
                  fontSize: 12,
                  fontWeight: 600,
                  color: active ? T.white : T.textSec,
                  background: active ? '#DC2626' : T.white,
                  border: `1px solid ${active ? '#DC2626' : T.border}`,
                  borderRadius: 20,
                  padding: '7px 14px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  boxShadow: T.shadow,
                  transition: 'background 0.15s, color 0.15s',
                }}
              >
                {chip}
              </button>
            );
          })}
        </div>
      </div>

      {/* The patient's own hospital record. In the app this IS the Hospitals
          screen, with the directory one tap away; here it is the other way
          round, and this card is the way across. */}
      {record?.linked && (
        <div style={{ padding: '14px 16px 0' }}>
          <button
            onClick={() => navigate('/patient/hospital')}
            style={{
              width: '100%', textAlign: 'left', cursor: 'pointer', fontFamily: T.font,
              background: `${T.teal}0F`, border: `1px solid ${T.teal}33`, borderRadius: 14,
              padding: 14, display: 'flex', alignItems: 'center', gap: 12,
            }}
          >
            <div style={{ width: 42, height: 42, borderRadius: 8, flexShrink: 0, background: `${T.teal}1F`, display: 'grid', placeItems: 'center' }}>
              <IconHospital color={T.navy} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 800, color: T.text }}>Your hospital record</div>
              <div style={{ fontSize: 11, color: T.textSec, marginTop: 2 }}>
                {[
                  record.activeEncounters > 0 ? `${record.activeEncounters} open ${record.activeEncounters === 1 ? 'visit' : 'visits'}` : null,
                  record.liveTokens > 0 ? `${record.liveTokens} queue ${record.liveTokens === 1 ? 'token' : 'tokens'} today` : null,
                  record.unpaidBills > 0 ? `${record.unpaidBills} unpaid ${record.unpaidBills === 1 ? 'bill' : 'bills'}` : null,
                ].filter(Boolean).join(' · ') || 'Visits, queue tokens and bills a hospital has shared with you'}
              </div>
            </div>
            <span style={{ fontSize: 16, color: T.teal }}>›</span>
          </button>
        </div>
      )}

      {/* Hospitals whose own panel is live. These are a different record from
          the marketplace cards below: they are maintained in the hospital's
          own app and are the only ones an appointment can be written into. */}
      {panels.length > 0 && (
        <div style={{ padding: '16px 16px 0' }}>
          <p style={{ fontFamily: T.font, fontSize: 13, fontWeight: 800, color: T.text, margin: '0 0 2px' }}>
            Hospitals you can book through the app
          </p>
          <p style={{ fontFamily: T.font, fontSize: 11, color: T.textTer, margin: '0 0 10px' }}>
            These publish their own departments, doctors and prices, and an appointment made here reaches their front desk.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {panels.map((h) => <PanelHospitalCard key={h.id} panel={h} />)}
          </div>
        </div>
      )}

      {/* Results */}
      <div style={{ padding: '16px 16px 0' }}>
        {loading ? (
          <div style={{ padding: '48px 0', textAlign: 'center' }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                border: `3px solid ${T.border}`,
                borderTopColor: '#DC2626',
                margin: '0 auto 12px',
                animation: 'spin 0.8s linear infinite',
              }}
            />
            <p style={{ fontFamily: T.font, fontSize: 13, color: T.textSec, margin: 0 }}>Loading hospitals...</p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : error ? (
          <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 12, padding: 16, textAlign: 'center' }}>
            <p style={{ fontFamily: T.font, fontSize: 13, color: '#B91C1C', margin: '0 0 10px' }}>{error}</p>
            <button
              onClick={fetchProviders}
              style={{ fontFamily: T.font, fontSize: 12, fontWeight: 700, color: T.white, background: '#DC2626', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer' }}
            >
              Retry
            </button>
          </div>
        ) : providers.length === 0 ? (
          <div style={{ padding: '48px 0', textAlign: 'center' }}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: '#DC262612',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 14px',
              }}
            >
              <IconHospital color="#DC2626" />
            </div>
            <p style={{ fontFamily: T.font, fontSize: 14, fontWeight: 700, color: T.text, margin: '0 0 6px' }}>
              No hospitals found
            </p>
            <p style={{ fontFamily: T.font, fontSize: 12, color: T.textSec, margin: 0 }}>
              {search ? `No results for "${search}".` : 'No hospitals available with current filters.'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ fontFamily: T.font, fontSize: 12, color: T.textSec, margin: '0 0 4px' }}>
              {providers.length} hospital{providers.length !== 1 ? 's' : ''} found
            </p>
            {providers.map((p) => (
              <HospitalCard key={p.id} provider={p} />
            ))}
          </div>
        )}
      </div>

      {/* NeoHealthCard branding */}
      <div style={{ padding: '20px 16px 8px', textAlign: 'center' }}>
        <span style={{ fontFamily: T.font, fontSize: 9, color: T.textTer, lineHeight: 1.6 }}>
          NeoHealthCard™ · Verified hospital network · Every booking is consent-based and audit logged
        </span>
      </div>
    </div>
  );
}
